import React, { useState, useEffect, useCallback, useRef } from 'react';
import GameBoard from './GameBoard';
import GameMenu from './GameMenu';
import GameUI from './GameUI';
import PauseScreen from './PauseScreen';
import GameOverScreen from './GameOverScreen';
import HelpScreen from './HelpScreen';
import OnlineMultiplayer from './OnlineMultiplayer';
import Statistics from './Statistics';
import UserNameScreen from './UserNameScreen';
import WebSocketService from '../services/websocketService';
import { checkWin, isBoardFull } from '../utils/gameLogic';
import { getSmartBotMove } from '../utils/fastAI';
import { updateStatsAfterGame } from '../utils/statsManager';
import { hasUsername, setUsername, getCurrentUsername } from '../utils/userManager';
import './Connect4Game.css';

const ROWS = 6;
const COLS = 7;

const Player = {
  NONE: 0,
  RED: 1,
  YELLOW: 2
};

const GameState = {
  USERNAME_SETUP: 'usernameSetup',
  MENU: 'menu',
  PLAYING: 'playing',
  GAME_OVER: 'gameOver',
  PAUSED: 'paused',
  HELP: 'help',
  TRANSITION: 'transition',
  ONLINE_MULTIPLAYER: 'onlineMultiplayer',
  STATISTICS: 'statistics'
};

const Connect4Game = () => {
  const [board, setBoard] = useState(() => 
    Array(ROWS).fill().map(() => Array(COLS).fill(Player.NONE))
  );
  const [currentPlayer, setCurrentPlayer] = useState(Player.RED);
  const [gameState, setGameState] = useState(() => {
    // Check if user has a username, if not show username setup first
    return hasUsername() ? GameState.MENU : GameState.USERNAME_SETUP;
  });
  const [gameMode, setGameMode] = useState('pvp'); // 'pvp', 'pvc', or 'online'
  const [botDifficulty, setBotDifficulty] = useState(3); // 3, 6, or 9
  const [winner, setWinner] = useState(null);
  const [isGameDraw, setIsGameDraw] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [currentCol, setCurrentCol] = useState(3);
  const [gameTime, setGameTime] = useState(0);
  const [playerNames, setPlayerNames] = useState({ red: 'Red Player', yellow: 'Yellow Player' });
  const [currentUsername, setCurrentUsername] = useState(() => getCurrentUsername());
  
  // Online multiplayer state
  const [isOnlineGame, setIsOnlineGame] = useState(false);
  const [onlinePlayerRole, setOnlinePlayerRole] = useState(null); // 'red' or 'yellow'
  const [isMyTurn, setIsMyTurn] = useState(false);
  
  // Use online game state variables
  const isOnlineMode = isOnlineGame && gameMode === 'online';
  const playerRole = onlinePlayerRole;
  
  // Debug logging for online game state
  if (process.env.NODE_ENV === 'development') {
    console.debug('Online game state:', { isOnlineMode, playerRole, isMyTurn });
  }
  
  // WebSocket service ref
  const webSocketServiceRef = useRef(null);
  const gameTimeRef = useRef(null);

  // Reset the game board
  const resetBoard = useCallback(() => {
    setBoard(Array(ROWS).fill().map(() => Array(COLS).fill(Player.NONE)));
    setCurrentPlayer(Player.RED);
    setCurrentCol(3);
    setWinner(null);
    setIsGameDraw(false);
    setIsThinking(false);
    setGameTime(0);
  }, []);

  // Handle online multiplayer move (from server)
  const handleOnlineMove = useCallback((col, playerId, playerColor) => {
    console.log('Handling online move:', { col, playerId, playerColor });
    
    // Find the lowest empty row in the column
    let row = -1;
    for (let r = ROWS - 1; r >= 0; r--) {
      if (board[r][col] === Player.NONE) {
        row = r;
        break;
      }
    }

    if (row === -1) {
      console.log('Column is full');
      return; // Column is full
    }

    // Determine which player made the move based on color
    const movePlayer = playerColor === 'red' ? Player.RED : Player.YELLOW;
    console.log('Move player:', movePlayer, 'Current player:', currentPlayer);

    // Make the move
    const newBoard = board.map(row => [...row]);
    newBoard[row][col] = movePlayer;
    setBoard(newBoard);

    // Set transition state
    setGameState(GameState.TRANSITION);
    
    setTimeout(() => {
      // Check for win or draw
      if (checkWin(newBoard, movePlayer)) {
        setWinner(movePlayer);
        setGameState(GameState.GAME_OVER);
        return;
      }

      if (isBoardFull(newBoard)) {
        setIsGameDraw(true);
        setGameState(GameState.GAME_OVER);
        return;
      }

      // Switch current player for next turn
      const nextPlayer = movePlayer === Player.RED ? Player.YELLOW : Player.RED;
      setCurrentPlayer(nextPlayer);
      
      // Determine if it's my turn based on player role and next player
      const myRole = webSocketServiceRef.current?.getStatus().playerRole;
      const isMyNextTurn = (nextPlayer === Player.RED && myRole === 'red') || 
                          (nextPlayer === Player.YELLOW && myRole === 'yellow');
      console.log('Next player:', nextPlayer, 'My role:', myRole, 'Is my turn:', isMyNextTurn);
      setIsMyTurn(isMyNextTurn);
      
      setGameState(GameState.PLAYING);
    }, 2000);
  }, [board, currentPlayer]);

  // Initialize WebSocket service
  useEffect(() => {
    // Initialize WebSocket service
    webSocketServiceRef.current = new WebSocketService();
    
    // Set up WebSocket event handlers
    webSocketServiceRef.current.on('gameStarted', (gameState) => {
      console.log('Game start event received:', gameState);
      console.log('Current game state before transition:', gameState);
      
      // Reset the game state for a fresh start
      resetBoard();
      setGameMode('online');
      setIsOnlineGame(true);
      setCurrentPlayer(Player.RED); // Red always starts
      setWinner(null);
      setIsGameDraw(false);
      setGameTime(0);
      
      // Determine player role based on stored role
      const playerRole = webSocketServiceRef.current.getStatus().playerRole;
      console.log('Player role:', playerRole);
      console.log('Setting isMyTurn to:', playerRole === 'red');
      setOnlinePlayerRole(playerRole); // This will be 'red' or 'yellow'
      setIsMyTurn(playerRole === 'red'); // Red player goes first
      
      // Transition to playing state
      console.log('Transitioning to PLAYING state');
      setGameState(GameState.PLAYING);
    });
    
    webSocketServiceRef.current.on('move', (move, gameState) => {
      // Handle moves from server with player color information
      console.log('Received move from server:', move);
      handleOnlineMove(move.column, move.playerId, move.playerColor);
    });
    
    webSocketServiceRef.current.on('gameOver', (winner, gameState) => {
      setWinner(winner === 'red' ? Player.RED : winner === 'yellow' ? Player.YELLOW : null);
      setGameState(GameState.GAME_OVER);
    });
    
    return () => {
      if (webSocketServiceRef.current) {
        webSocketServiceRef.current.disconnect();
      }
    };
  }, [handleOnlineMove, resetBoard]);

  // Game timer (updates every 10ms for millisecond precision)
  // Timer runs during PLAYING and TRANSITION states, stops only when PAUSED or game ends
  useEffect(() => {
    if (gameState === GameState.PLAYING || gameState === GameState.TRANSITION) {
      gameTimeRef.current = setInterval(() => {
        setGameTime(prev => prev + 10);
      }, 10);
    } else {
      if (gameTimeRef.current) {
        clearInterval(gameTimeRef.current);
      }
    }

    return () => {
      if (gameTimeRef.current) {
        clearInterval(gameTimeRef.current);
      }
    };
  }, [gameState]);

  // Handle username setup
  const handleUsernameSet = useCallback((username) => {
    setUsername(username);
    setCurrentUsername(username);
    setGameState(GameState.MENU);
  }, []);

  // Start new game
  const startNewGame = useCallback((mode, difficulty = 3, names = { red: getCurrentUsername() || 'Red Player', yellow: 'Yellow Player' }) => {
    setGameMode(mode);
    setBotDifficulty(difficulty);
    setPlayerNames(names);
    resetBoard();
    setGameState(GameState.PLAYING);
  }, [resetBoard]);

  // Handle column click
  const handleColumnClick = useCallback((col, isBotMove = false) => {
    // Check basic conditions
    if (gameState !== GameState.PLAYING || isThinking) return;
    
    // Check turn restrictions (allow bot moves)
    if (gameMode === 'pvc' && currentPlayer === Player.YELLOW && !isBotMove) return;
    if (gameMode === 'online' && !isMyTurn) return;
    
    // Find the lowest empty row in the column
    let row = -1;
    for (let r = ROWS - 1; r >= 0; r--) {
      if (board[r][col] === Player.NONE) {
        row = r;
        break;
      }
    }

    if (row === -1) return; // Column is full

    // For online games, send move to server and wait for confirmation
    if (gameMode === 'online' && webSocketServiceRef.current) {
      console.log('Sending move to server:', col, 'Is my turn:', isMyTurn);
      webSocketServiceRef.current.makeMove(col);
      setIsMyTurn(false); // Not my turn anymore
      return; // Don't update local board, wait for server response
    }

    // Make the move locally (for PvP and PvC modes only)
    const newBoard = board.map(row => [...row]);
    newBoard[row][col] = currentPlayer;
    setBoard(newBoard);

    // Set transition state for 2 seconds
    setGameState(GameState.TRANSITION);
    
    setTimeout(() => {
      // Check for win or draw
      if (checkWin(newBoard, currentPlayer)) {
        setWinner(currentPlayer);
        setGameState(GameState.GAME_OVER);
        
        // Only track scores for local games
        if (gameMode !== 'online') {
          const gameResult = {
            mode: gameMode,
            winner: currentPlayer,
            time: gameTime,
            difficulty: gameMode === 'pvc' ? botDifficulty : null,
            playerNames: playerNames
          };

          // Update and save stats using the stats manager
          updateStatsAfterGame(gameResult);
        }
        return;
      }

      if (isBoardFull(newBoard)) {
        setIsGameDraw(true);
        setGameState(GameState.GAME_OVER);
        
        // Only track scores for local games
        if (gameMode !== 'online') {
          const gameResult = {
            mode: gameMode,
            winner: null,
            time: gameTime,
            difficulty: gameMode === 'pvc' ? botDifficulty : null,
            playerNames: playerNames
          };

          // Update and save stats using the stats manager
          updateStatsAfterGame(gameResult);
        }
        return;
      }

      // Switch player and continue game
      setCurrentPlayer(currentPlayer === Player.RED ? Player.YELLOW : Player.RED);
      setGameState(GameState.PLAYING);
    }, 2000);
  }, [board, currentPlayer, gameState, isThinking, gameMode, botDifficulty, gameTime, playerNames, isMyTurn]);


  useEffect(() => {
    const handleKeyPress = (event) => {
      // Check basic conditions
      if (gameState !== GameState.PLAYING || isThinking) return;
      
      // Check turn restrictions
      if (gameMode === 'pvc' && currentPlayer === Player.YELLOW) return;
      if (gameMode === 'online' && !isMyTurn) return;

      switch (event.key) {
        case 'ArrowLeft':
          setCurrentCol(prev => Math.max(0, prev - 1));
          break;
        case 'ArrowRight':
          setCurrentCol(prev => Math.min(COLS - 1, prev + 1));
          break;
        case 'ArrowDown':
        case ' ':
          handleColumnClick(currentCol, false);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState, isThinking, gameMode, currentPlayer, currentCol, handleColumnClick, isMyTurn]);

  // Bot move effect 
  useEffect(() => {
    if (gameState === GameState.PLAYING && 
        gameMode === 'pvc' && 
        currentPlayer === Player.YELLOW && 
        !isThinking) {
      
      // Get bot move based on difficulty level
      let botMove;
      if (botDifficulty === 3) {
        // Rookie: Depth 6 - Moderate strategic thinking
        botMove = getSmartBotMove(board, 6);
      } else if (botDifficulty === 6) {
        // Warrior: Depth 9 - Strong tactical play
        botMove = getSmartBotMove(board, 9);
      } else {
        // Master: Depth 12 - Expert level play
        botMove = getSmartBotMove(board, 12);
      }
      
      if (botMove !== -1) {
        setCurrentCol(botMove);
        // Execute move immediately without any delay - pass isBotMove flag
        handleColumnClick(botMove, true);
      }
    }
  }, [currentPlayer, gameState, gameMode, board, handleColumnClick, isThinking, botDifficulty]);

  // Handle game state changes
  const handleMenuAction = useCallback((action, data) => {
    switch (action) {
      case 'playVsPlayer':
        startNewGame('pvp', 3, data?.names || { red: 'Red Player', yellow: 'Yellow Player' });
        break;
      case 'playVsComputer':
        startNewGame('pvc', data?.difficulty || 3, { red: 'Player', yellow: 'Bot' });
        break;
      case 'onlineMultiplayer':
        setGameState(GameState.ONLINE_MULTIPLAYER);
        break;
      case 'startOnlineGame':
        // This will be called from the OnlineMultiplayer component
        setGameMode('online');
        setIsOnlineGame(true);
        setGameState(GameState.PLAYING);
        break;
      case 'help':
        setGameState(GameState.HELP);
        break;
      case 'showStats':
        setGameState(GameState.STATISTICS);
        break;
      case 'menu':
        setGameState(GameState.MENU);
        break;
      case 'setDifficulty':
        setBotDifficulty(data.difficulty);
        break;
      case 'exit':
        window.close();
        break;
      default:
        break;
    }
  }, [startNewGame]);

  const handleGameAction = useCallback((action) => {
    switch (action) {
      case 'pause':
        setGameState(GameState.PAUSED);
        break;
      case 'resume':
        setGameState(GameState.PLAYING);
        break;
      case 'restart':
        resetBoard();
        setGameState(GameState.PLAYING);
        break;
      case 'menu':
        setGameState(GameState.MENU);
        break;
      case 'newGame':
        resetBoard();
        setGameState(GameState.PLAYING);
        break;
      case 'exit':
        setGameState(GameState.MENU);
        break;
      default:
        break;
    }
  }, [resetBoard]);

  return (
    <div className="connect4-game">
      <div 
        className="game-background"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/backgroundab5.jpg)`
        }}
      >
        {gameState === GameState.USERNAME_SETUP && (
          <UserNameScreen 
            onUsernameSet={handleUsernameSet}
            currentUsername={currentUsername}
          />
        )}

        {gameState === GameState.MENU && (
          <GameMenu 
            onAction={handleMenuAction} 
            botDifficulty={botDifficulty}
          />
        )}
        
        {gameState === GameState.STATISTICS && (
          <Statistics
            onAction={handleMenuAction}
          />
        )}
        
        {gameState === GameState.HELP && (
          <HelpScreen 
            onAction={handleMenuAction}
          />
        )}

        {gameState === GameState.ONLINE_MULTIPLAYER && (
          <OnlineMultiplayer 
            onAction={handleMenuAction}
            webSocketService={webSocketServiceRef.current}
            currentUsername={currentUsername}
          />
        )}

        {gameState === GameState.PAUSED && (
          <PauseScreen 
            onAction={handleGameAction}
          />
        )}

        {gameState === GameState.GAME_OVER && (
          <GameOverScreen
            winner={winner}
            isGameDraw={isGameDraw}
            gameMode={gameMode}
            playerNames={playerNames}
            gameTime={gameTime}
            onAction={handleGameAction}
          />
        )}
        
        {(gameState === GameState.PLAYING || gameState === GameState.TRANSITION) && (
          <div className="game-container">
            <GameUI 
              currentPlayer={currentPlayer}
              gameState={gameState}
              gameMode={gameMode}
              playerNames={playerNames}
              gameTime={gameTime}
              isThinking={isThinking}
              isMyTurn={isMyTurn}
              onlinePlayerRole={onlinePlayerRole}
              onAction={handleGameAction}
            />
            
            <GameBoard 
              board={board}
              onColumnClick={handleColumnClick}
              currentPlayer={currentPlayer}
              currentCol={currentCol}
              gameState={gameState}
              isThinking={isThinking}
              gameMode={gameMode}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Connect4Game;
